import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { getClassroomByInviteToken } from '../services/classroomService.js';
import '../styles/ClassroomInviteLanding.css';

const getExpiresAtDate = (expiresAt) => {
  if (!expiresAt) {
    return null;
  }

  if (expiresAt instanceof Timestamp) {
    return expiresAt.toDate();
  }

  if (expiresAt.toDate && typeof expiresAt.toDate === 'function') {
    return expiresAt.toDate();
  }

  const parsed = new Date(expiresAt);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildClassroomSummary = (classroom) => {
  if (!classroom) {
    return null;
  }

  const { name, description, categoryIds } = classroom;

  return {
    name: name || 'Sala sem nome',
    description: description || 'O professor ainda não forneceu uma descrição para esta sala.',
    categories: Array.isArray(categoryIds) ? categoryIds : []
  };
};

function ClassroomInviteLanding() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    status: 'loading',
    classroom: null,
    invite: null,
    isExpired: false,
    error: null
  });

  useEffect(() => {
    let isMounted = true;

    const loadInvite = async () => {
      if (!inviteId) {
        if (isMounted) {
          setState({
            status: 'error',
            classroom: null,
            invite: null,
            isExpired: false,
            error: 'Convite inválido. Verifique se o link está correto.'
          });
        }
        return;
      }

      setState((prev) => ({ ...prev, status: 'loading', error: null }));

      try {
        const data = await getClassroomByInviteToken(inviteId);

        if (!isMounted) {
          return;
        }

        if (!data || !data.invite || !data.invite.token) {
          setState({
            status: 'error',
            classroom: null,
            invite: null,
            isExpired: false,
            error: 'Convite não encontrado ou já foi revogado.'
          });
          return;
        }

        const expiresAt = getExpiresAtDate(data.invite.expiresAt);
        const expired = expiresAt ? expiresAt.getTime() < Date.now() : false;

        setState({
          status: 'ready',
          classroom: data.classroom || null,
          invite: data.invite,
          isExpired: expired,
          error: expired
            ? 'Este convite expirou. Solicite um novo link ao professor.'
            : null
        });
      } catch (error) {
        console.error('Erro ao carregar convite da sala:', error);
        if (isMounted) {
          setState({
            status: 'error',
            classroom: null,
            invite: null,
            isExpired: false,
            error: 'Não foi possível carregar as informações do convite. Tente novamente mais tarde.'
          });
        }
      }
    };

    loadInvite();

    return () => {
      isMounted = false;
    };
  }, [inviteId]);

  const classroomSummary = useMemo(
    () => buildClassroomSummary(state.classroom),
    [state.classroom]
  );

  const inviteContext = useMemo(() => {
    if (!state.invite?.token) {
      return null;
    }

    return {
      redirectTo: `/classrooms/${state.invite.token}/join`,
      intent: 'accept-classroom-invite',
      inviteId: state.invite.token,
      classroomId: state.classroom?.id ?? null
    };
  }, [state.invite?.token, state.classroom?.id]);

  const canInteract =
    state.status === 'ready' &&
    !state.isExpired &&
    Boolean(inviteContext);

  const handleNavigateToLogin = useCallback(() => {
    if (!canInteract || !inviteContext) {
      return;
    }

    navigate('/login', { state: inviteContext });
  }, [canInteract, inviteContext, navigate]);

  const handleNavigateToSignup = useCallback(() => {
    if (!canInteract || !inviteContext) {
      return;
    }

    navigate('/subscribe/student', { state: inviteContext });
  }, [canInteract, inviteContext, navigate]);

  return (
    <div className="invite-landing">
      <div className="invite-landing__card">
        {state.status === 'loading' && (
          <div className="invite-landing__loading">
            <div className="invite-landing__spinner" aria-hidden="true" />
            <p>Carregando informações da sala...</p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="invite-landing__error" role="alert">
            <h1>Convite inválido</h1>
            <p>{state.error}</p>
          </div>
        )}

        {state.status === 'ready' && (
          <div className="invite-landing__content">
            <header className="invite-landing__header">
              <h1>Você foi convidado para uma sala</h1>
              {state.isExpired && (
                <span className="invite-landing__badge invite-landing__badge--expired">
                  Convite expirado
                </span>
              )}
            </header>

            <section className="invite-landing__section">
              <h2>{classroomSummary?.name}</h2>
              <p>{classroomSummary?.description}</p>

              {classroomSummary?.categories?.length > 0 && (
                <div className="invite-landing__categories">
                  <span>Categorias:</span>
                  <ul>
                    {classroomSummary.categories.map((category) => (
                      <li key={category}>{category}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

              <section className="invite-landing__section invite-landing__section--actions">
                {state.isExpired && (
                  <p className="invite-landing__notice" role="status">
                    {state.error}
                  </p>
                )}
                <p>
                  Para acessar a sala, continue para entrar com sua conta ou criar um cadastro.
                </p>
                <div className="invite-landing__actions" aria-live="polite">
                  <button
                    type="button"
                    className="invite-landing__button"
                    disabled={!canInteract}
                    onClick={handleNavigateToLogin}
                  >
                    Já tenho conta
                  </button>
                  <button
                    type="button"
                    className="invite-landing__button invite-landing__button--primary"
                    disabled={!canInteract}
                    onClick={handleNavigateToSignup}
                  >
                    Quero me cadastrar
                  </button>
                </div>
                <small>
                  Você será direcionado(a) para realizar login ou criar sua conta. Após concluir, retornaremos automaticamente para esta página.
                </small>
              </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClassroomInviteLanding;
